import {
  Badge,
  BodyText,
  Button,
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
} from "@/components/ui";
import { getQuestionnaireCategory } from "@/network/useQuestionnaires";
import type {
  LifestyleQuestionnaireTypes,
  MentalHealthQuestionnaireTypes,
} from "@/types/questionnaires";
import { QuestionnaireResponse } from "@curai/js-fhir-tools";
import { useMemo } from "react";
import {
  useConfirmQuestionnaire,
  useGetQuestionnaireById,
} from "../../network";
import { formatAuthoredDate } from "../../utils";
import { useQuestionnaireHistory } from "../QuestionnaireHistoryModal/useQuestionnaireHistory";
import { QuestionnaireDetailDialogFooter } from "./QuestionnaireDetailDialogFooter";
import { QuestionnaireDetailDialogQuestions } from "./QuestionnaireDetailDialogQuestions";
import {
  QuestionnaireDetailDialogFooterSkeleton,
  QuestionnaireDetailDialogQuestionsSkeleton,
} from "./QuestionnaireDetailDialogSkeleton";

interface SpecificQuestionnaireDetailDialogProps {
  selectedResponseId: string;
  onSelectedResponseIdChange: (id: string) => void;
  questionnaireType:
    | LifestyleQuestionnaireTypes
    | MentalHealthQuestionnaireTypes;
  patientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const QuestionnaireHistoryDetailDialog = ({
  selectedResponseId,
  onSelectedResponseIdChange,
  questionnaireType,
  patientId,
  open,
  onOpenChange,
}: SpecificQuestionnaireDetailDialogProps) => {
  // Get History for this questionnaire type and patient from the API
  const {
    data: questionnaireHistoryData,
    isLoading: isQuestionnaireHistoryLoading,
    isError: isQuestionnaireHistoryError,
    refetch: refetchQuestionnaireHistory,
  } = useQuestionnaireHistory({
    patientId,
    questionnaireType: questionnaireType,
    enabled: open,
  });

  const selectedHistoryItem =
    questionnaireHistoryData?.questionnaireHistory.find(
      (item) => item.questionnaireResponseId === selectedResponseId,
    );

  // Get the questionnaire response from the API using the selected ID
  const {
    data: questionnaireResponseData,
    isLoading: isQuestionnaireResponseLoading,
    isError: isQuestionnaireResponseError,
    refetch: refetchQuestionnaireResponse,
  } = useGetQuestionnaireById(selectedResponseId, patientId, open);
  const questionnaireResponse =
    questionnaireResponseData?.questionnaireResponse;

  // Check if the questionnaire has been reviewed by looking for confirmation data
  const isReviewed = useMemo(() => {
    if (!questionnaireResponse) return false;
    const qr = new QuestionnaireResponse(questionnaireResponse);
    return (
      qr.getConfirmationActorName() !== undefined &&
      qr.getConfirmationOccurredDate() !== undefined
    );
  }, [questionnaireResponse]);
  const isCompleted = questionnaireResponse?.status === "completed";
  const isReviewable = !isReviewed && isCompleted;

  const confirmQuestionnaire = useConfirmQuestionnaire({
    questionnaireResponseId: questionnaireResponse?.id || "dummy-id",
    patientId: patientId || "dummy-patient",
    questionnairesCategory: getQuestionnaireCategory(questionnaireType),
    questionnaireType: questionnaireType,
    onSuccess: () => {},
    onError: (error) => {
      const isIncomplete = error?.response?.status === 422;
      const errorMessage = isIncomplete
        ? "Confirmation failed: questionnaire incomplete"
        : "Confirmation failed";
      toast.error(errorMessage);
    },
  });

  if (
    !questionnaireResponse &&
    !isQuestionnaireResponseLoading &&
    !isQuestionnaireResponseError
  ) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay />
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex-shrink-0 space-y-1">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle asChild>
                <BodyText size="md" bold>
                  {questionnaireResponseData?.questionnaireShortLabel}{" "}
                  Questionnaire
                </BodyText>
              </DialogTitle>
              <DialogDescription className="hidden">
                Questionnaire Responses
              </DialogDescription>
            </div>
            {isQuestionnaireHistoryLoading && (
              <Skeleton className="w-full h-10" />
            )}
            {isQuestionnaireHistoryError && (
              <Callout
                className="w-full"
                variant="danger"
                title="Error loading questionnaire history asdf"
                description="Please click here to try again."
                onClick={() => {
                  refetchQuestionnaireHistory();
                }}
              />
            )}
            {questionnaireHistoryData &&
              questionnaireHistoryData.questionnaireHistory.length > 0 && (
                <div className="flex items-center justify-between pt-2">
                  <Select
                    value={selectedResponseId}
                    onValueChange={onSelectedResponseIdChange}
                  >
                    <SelectTrigger className="w-auto">
                      <SelectValue placeholder="Select a questionnaire response">
                        {selectedHistoryItem && (
                          <span className="flex items-center gap-4">
                            <span>
                              {formatAuthoredDate(
                                selectedHistoryItem.authored ?? "",
                              )}
                            </span>
                            {selectedHistoryItem.confirmationMetadata
                              ?.occurred === undefined && (
                              <Badge variant="warning" className="w-fit">
                                <BodyText textColor="warning" size="sm">
                                  Not Reviewed
                                </BodyText>
                              </Badge>
                            )}
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {questionnaireHistoryData.questionnaireHistory.map(
                        (item) => (
                          <SelectItem
                            key={item.questionnaireResponseId}
                            value={item.questionnaireResponseId}
                            rightSlot={
                              item.confirmationMetadata?.occurred ===
                              undefined ? (
                                <Badge variant="warning" className="w-fit">
                                  <BodyText textColor="warning" size="sm">
                                    Not Reviewed
                                  </BodyText>
                                </Badge>
                              ) : undefined
                            }
                          >
                            {formatAuthoredDate(item.authored ?? "")}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  {isReviewable && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => confirmQuestionnaire.mutate(undefined)}
                        isLoading={confirmQuestionnaire.isPending}
                        disabled={
                          confirmQuestionnaire.isPending ||
                          !questionnaireResponse?.id
                        }
                      >
                        Confirm Review
                      </Button>
                    </div>
                  )}
                </div>
              )}
          </DialogHeader>
        </div>
        {isQuestionnaireResponseLoading ? (
          <>
            <QuestionnaireDetailDialogQuestionsSkeleton />
            <QuestionnaireDetailDialogFooterSkeleton />
          </>
        ) : (
          <>
            {isQuestionnaireResponseError && (
              <Callout
                className="w-full"
                variant="danger"
                title="Error loading questionnaire response"
                description="Please click here to try again."
                onClick={() => {
                  refetchQuestionnaireResponse();
                }}
              />
            )}
            {questionnaireResponse && (
              <>
                <QuestionnaireDetailDialogQuestions
                  questionnaireResponse={questionnaireResponse}
                  totalQuestions={questionnaireResponseData?.questionCount ?? 0}
                  questionnaireId={questionnaireType}
                />
                <QuestionnaireDetailDialogFooter
                  questionnaireResponse={questionnaireResponse}
                  responseScore={questionnaireResponseData?.responseScore}
                  maxScore={questionnaireResponseData?.maxScore}
                />
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
